import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useRef } from "react";
import { Bold, Italic, List, ListOrdered, Heading2, Heading3, Quote, Undo2, Redo2 } from "lucide-react";

/**
 * Goldline-branded Tiptap rich-text editor.
 *
 * Props:
 *   value      — initial HTML string
 *   onChange   — fired on every change with the latest HTML
 *   placeholder — visible when empty (CSS-only)
 *   minHeight  — min editor height in px
 */
export default function RichTextEditor({ value = "", onChange, placeholder = "Start typing your notes...", minHeight = 220 }) {
  const lastEmittedRef = useRef(value);

  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] } })],
    content: value || "",
    editorProps: {
      attributes: {
        class: "tiptap-content prose prose-sm max-w-none focus:outline-none px-4 py-3 text-navy",
        "data-placeholder": placeholder,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastEmittedRef.current = html;
      onChange?.(html);
    },
  });

  // Keep editor in sync if `value` updates externally (e.g., reload from server)
  useEffect(() => {
    if (!editor) return;
    if (value !== lastEmittedRef.current && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
      lastEmittedRef.current = value;
    }
  }, [value, editor]);

  if (!editor) return null;

  const Btn = ({ active, onClick, children, label }) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`p-1.5 border ${active ? "bg-navy text-ivory border-navy" : "bg-white text-navy/70 border-navy/15 hover:border-gold hover:text-navy"} rounded-none transition`}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-navy/15 bg-white rounded-none">
      <div className="flex flex-wrap gap-1 p-2 border-b border-navy/10 bg-ivory/40">
        <Btn label="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 className="w-4 h-4" />
        </Btn>
        <Btn label="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 className="w-4 h-4" />
        </Btn>
        <span className="w-px bg-navy/10 mx-1" />
        <Btn label="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="w-4 h-4" />
        </Btn>
        <Btn label="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="w-4 h-4" />
        </Btn>
        <span className="w-px bg-navy/10 mx-1" />
        <Btn label="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="w-4 h-4" />
        </Btn>
        <Btn label="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="w-4 h-4" />
        </Btn>
        <Btn label="Blockquote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote className="w-4 h-4" />
        </Btn>
        <span className="w-px bg-navy/10 mx-1" />
        <Btn label="Undo" onClick={() => editor.chain().focus().undo().run()}>
          <Undo2 className="w-4 h-4" />
        </Btn>
        <Btn label="Redo" onClick={() => editor.chain().focus().redo().run()}>
          <Redo2 className="w-4 h-4" />
        </Btn>
      </div>
      <div style={{ minHeight }}>
        <EditorContent editor={editor} data-testid="rich-text-editor" />
      </div>
    </div>
  );
}
